import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragHandleHorizontalIcon } from "@radix-ui/react-icons";

import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { cn } from "~/lib/utils";

const TINY_PREVIEW_PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEoAAABgAgMAAADHiQyyAAAACVBMVEX////d3d3w8PBpbxxxAAAALUlEQVR4AWMYVIAxFBWMmocFjJo3al4IVulVIECKGMI8iBiF5o2aRxIYBaMAAKrIhi+WC/DIAAAAAElFTkSuQmCC";

interface DocListPageProps {
  pageId: string;
  pageNumber: number;
  pageImg: string | null;
  sectionId: string;
  isDraggable: boolean;
  index: number;
  isDragOverlay?: boolean;
  isSelected: boolean;
  absolutePageNumbers: Record<string, number>;
  onSelect: (pageId: string) => void;
  rotation?: number;
}

export const DocListPage = ({
  pageId,
  pageNumber,
  pageImg = TINY_PREVIEW_PLACEHOLDER,
  sectionId,
  isDraggable,
  index,
  isDragOverlay = false,
  isSelected,
  absolutePageNumbers,
  rotation = 0,
}: DocListPageProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: pageId,
      data: {
        type: "page",
        pageId,
        sectionId,
        index,
      },
      disabled: !isDraggable || isDragOverlay,
    });
  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: "none",
      }
    : undefined;

  const [{ pageId: currentPageId }, updateParams] = useDocumentSearchParams();

  const isCurrentPage = currentPageId === pageId;

  const handleClick = (e: React.MouseEvent) => {
    if (absolutePageNumbers[pageId]) {
      e.stopPropagation();
      updateParams({ pageId, sectionId });
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        onClick={handleClick}
        className={cn(
          "relative w-12 h-12 flex items-end justify-center text-xs border rounded-md overflow-hidden transition-all group cursor-pointer select-none",
          isDragOverlay ? "z-50" : "",
          isSelected ? "border-blue-600" : "border-base",
          !isDraggable &&
            "hover:shadow-md hover:border-blue-300 hover:scale-105 active:scale-95 transition-transform duration-150",
          isCurrentPage &&
            !isSelected &&
            "shadow-md border-blue-300 scale-105 active:scale-95 transition-transform duration-150"
        )}
      >
        <img
          src={pageImg ?? undefined}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain disable-default-drag"
          style={{
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
            willChange: rotation ? "transform" : undefined,
          }}
        />
        {isDraggable && (
          <>
            {/* Drag Handle */}
            <div className="absolute top-0 left-0 w-full h-full flex items-end justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-20 rounded-md">
              <div
                {...listeners}
                className="w-full h-[25px] flex items-center justify-center relative z-20 cursor-grab"
              >
                <DragHandleHorizontalIcon className="w-4 h-4" />
              </div>
            </div>
          </>
        )}
        <span
          className={`
            text-xs z-10 bg-white rounded-[10px] px-[6px] py-[2px] mb-1 text-muted-foreground transition-opacity
            ${isDraggable ? "group-hover:opacity-0" : ""}
          `}
        >
          {pageNumber}
        </span>
      </div>
    </>
  );
};
